import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CompiereLoginJSON } from '@compiere-ws/models/compiere-login-json';
import { IAutocomplete } from '@iupics-components/models/autocomplete-interfaces';
import { AppConfig, ModuleConfig } from '@iupics-config/app.config.service';
import { UserPreference } from '@web-desktop/models/user-preference';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CompiereLoginService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(AppConfig);

  private loginInfoUrl: string;
  private ctxUrl: string;
  private roleCtxUrl: string;
  private prefUrl: string;
  private resetUrl: string;
  private organizationsUrl: string;
  private warehousesUrl: string;

  loadUrl() {
    this.loginInfoUrl = this.config.getBackendResource('sessionInfo');
    this.ctxUrl = this.config.getBackendResource('ctx');
    this.roleCtxUrl = this.config.getBackendResource('roleCtx');
    this.prefUrl = this.config.getBackendResource('preference');
    this.resetUrl = this.config.getBackendResource('cachereset');
    this.organizationsUrl = this.config.getBackendResource('organisationsCTX');
    this.warehousesUrl = this.config.getBackendResource('warehousesCTX');
  }

  getLoginInfo(access_token: string): Observable<CompiereLoginJSON> {
    return this.http.get<any>(this.loginInfoUrl, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + access_token,
      }),
    });
  }

  getRoleCtx(roleId: number) {
    return this.http.get<any>(`${this.roleCtxUrl}/${roleId}`).pipe(map((ctx) => this.handleCtx(ctx)));
  }

  getCtx(): Observable<any> {
    return this.http.get<any>(this.ctxUrl).pipe(map((ctx) => this.handleCtx(ctx)));
  }

  handleCtx(ctx: any) {
    if (!ctx) return undefined;

    try {
      const keys = Object.keys(ctx);
      for (const key of keys) {
        ctx[key] = ctx[key] === null || isNaN(ctx[key]) ? ctx[key] : parseInt(ctx[key], 10);
        if (key.startsWith('apiz.modules')) {
          // charge la configuration des modules
          let configKey = key.replace('apiz.modules.', '');
          const moduleName = configKey.substring(0, configKey.indexOf('.'));
          configKey = configKey.replace(moduleName + '.', '');
          const moduleConfig = this.getModuleConfig(configKey, ctx[key]);
          this.config.addModuleConfig(moduleName, moduleConfig);
          delete ctx[key];
        }
      }
    } catch (_) {} // NOSONAR

    return ctx;
  }

  getModuleConfig(configKey: string, value: any): ModuleConfig {
    const keys = configKey.split('.');
    const returnObj: ModuleConfig = {};
    let objToLoop = returnObj; // on transmet la référence de returnObj à objToLoop
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const isLast = i === keys.length - 1;
      objToLoop[key] = isLast ? value : {};
      objToLoop = objToLoop[key];
    }
    return returnObj;
  }

  updateCtx(ctx: any): Observable<any> {
    return this.http.put<any>(this.ctxUrl, ctx).pipe(
      map((ctx_response) => {
        try {
          if (ctx_response) {
            const keys = Object.keys(ctx_response);
            for (const key of keys) {
              ctx_response[key] =
                ctx_response[key] === null || isNaN(ctx_response[key])
                  ? ctx_response[key]
                  : parseInt(ctx_response[key], 10);
            }
          }
        } catch (_) {} // NOSONAR

        return ctx_response;
      })
    );
  }

  savePref(userPref: UserPreference): Observable<any> {
    return this.http.post<CompiereLoginJSON>(this.prefUrl, userPref);
  }

  resetCache(): Observable<any> {
    return this.http.get<any>(this.resetUrl);
  }

  getOrganizations(): Observable<IAutocomplete[]> {
    return this.http.get<IAutocomplete[]>(this.organizationsUrl);
  }

  getWarehouses(): Observable<IAutocomplete[]> {
    return this.http.get<IAutocomplete[]>(this.warehousesUrl);
  }
}
